// Reports — pharmacy analytics + audit-export surface.
//
// Two sections:
//
//   1. Analytics — chart-driven view of delivery performance over a
//      selectable time window. KPIs (delivered / on-time / failure /
//      cold-chain compliance), volume trend bars, failure-reason
//      breakdown, driver leaderboard. This is the "how is the
//      pharmacy doing" surface — pharmacy owner comes here to answer
//      board / auditor / investor questions.
//
//   2. Audit manifest CSV — the pre-existing compliance export.
//      Unchanged behavior: date-ranged, one row per order, capped at
//      10,000 rows. Per-delivery signed PDFs stay in the order
//      detail flow.
//
// v0 preview: analytics numbers are placeholders. Wired to real
// aggregations in a follow-up PR (a `pharmacy_analytics_summary`
// RPC that returns KPI counts, per-day volume, reason breakdown,
// driver performance in one round trip). CSV export is real today.
//
// Deferred to later PRs (in rough priority order):
//   - The aggregation RPC + real numbers
//   - Cost-per-delivery once billing data lands
//   - DEA schedule breakdown (Schedule II count) chart
//   - Bulk-PDF ZIP export
//   - Per-drug / per-schedule CSV filter
//   - Saved report presets (Q1/Q2/Q3/Q4 shortcuts)
//   - Audit trail of who exported what and when

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth/auth-context';
import { requireUserType } from '../lib/auth/guards';
import { Alert } from '../components/ui/Alert';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';

export const Route = createFileRoute('/_authenticated/reports')({
  beforeLoad: async () => {
    await requireUserType({ allowed: ['pharmacy_admin', 'pharmacy_staff'] });
  },
  component: ReportsPage,
});

function ReportsPage(): JSX.Element {
  useSetPageHeader({
    title: 'Reports',
    subtitle: 'Analytics + compliance exports.',
  });
  return <ReportsView />;
}

// ---------------------------------------------------------------------------
// Range picker

type RangeKey = '7d' | '30d' | '90d' | 'mtd' | 'custom';

const RANGES: Array<{ key: RangeKey; label: string; days: number | null }> = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: 'mtd', label: 'Month to date', days: null },
  { key: 'custom', label: 'Custom', days: null },
];

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function firstOfMonthIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function daysAgoIso(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// ---------------------------------------------------------------------------
// Placeholder analytics data — swap for real aggregation query in
// follow-up PR. Shape here is what the RPC should return.

const PLACEHOLDER_KPIS = {
  delivered: { value: 1247, delta: '+8% vs prev period', tone: 'up' as const },
  onTimeRate: { value: 94, delta: '+2 pts', tone: 'up' as const },
  failureRate: { value: 3.2, delta: '-0.4 pts', tone: 'down' as const },
  coldChainCompliance: { value: 99.7, delta: '─ stable', tone: 'neutral' as const },
};

// 30 daily buckets, most-recent-last. Values shaped like a real
// pharmacy week (Mon-Fri active, weekend dip).
const PLACEHOLDER_VOLUME: number[] = [
  38, 42, 45, 39, 51, 14, 9,
  41, 46, 44, 40, 53, 15, 11,
  43, 48, 46, 42, 55, 16, 12,
  47, 51, 49, 46, 58, 18, 13,
  50, 54,
];

const PLACEHOLDER_REASONS: Array<{ label: string; count: number }> = [
  { label: 'Nobody home', count: 22 },
  { label: 'Wrong address', count: 11 },
  { label: 'Refused delivery', count: 6 },
  { label: 'Cold-chain deviation', count: 3 },
  { label: 'Damaged package', count: 2 },
];

// ---------------------------------------------------------------------------

export function ReportsView(): JSX.Element {
  const { session, isReady } = useAuth();

  // ?demo=1 in the URL flips the placeholder analytics on. Same
  // pattern as /exceptions. Default OFF so the pharmacy sees an
  // honest "not enough data yet" state instead of made-up KPIs;
  // enable with ?demo=1 for prospect demos, screenshots, or QA.
  // Tree-shaken read-only via URLSearchParams — never touches the
  // DB, never persists.
  const demoMode =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('demo') === '1';

  // Analytics range state — separate from the CSV export's range so
  // the pharmacist can eyeball a rolling 30-day view while pulling a
  // monthly manifest for audit.
  const [range, setRange] = useState<RangeKey>('30d');
  const analyticsRangeLabel = useMemo(() => {
    const r = RANGES.find((x) => x.key === range);
    return r?.label ?? 'Last 30 days';
  }, [range]);

  // CSV export state — preserved verbatim from prior Reports page.
  const [from, setFrom] = useState<string>(firstOfMonthIso());
  const [to, setTo] = useState<string>(todayIso());
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastExportInfo, setLastExportInfo] = useState<{
    filename: string;
    rangeLabel: string;
  } | null>(null);

  const rangeValid = useMemo(
    () => DATE_RE.test(from) && DATE_RE.test(to) && from <= to,
    [from, to],
  );

  const handleExport = useCallback(async () => {
    if (!isReady || !session || !rangeValid) return;
    setDownloading(true);
    setError(null);
    try {
      const url = `/api/audit/export.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({ error: 'unknown' }));
        throw new Error(String(body.error ?? resp.statusText));
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('content-disposition') ?? '';
      const match = cd.match(/filename="([^"]+)"/);
      const filename = match?.[1] ?? `lynrx-audit-export-${from}-${to}.csv`;

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);

      setLastExportInfo({
        filename,
        rangeLabel: `${from} → ${to}`,
      });
    } catch (e) {
      setError(
        e instanceof Error
          ? `Export failed: ${e.message}`
          : 'Export failed. Please try again.',
      );
    } finally {
      setDownloading(false);
    }
  }, [isReady, session, rangeValid, from, to]);

  return (
    <div className="space-y-6">
      {demoMode ? (
        <>
          <PreviewBanner />
          <RangeTabs value={range} onChange={setRange} />
          <KpiRow />
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <VolumeTrendCard rangeLabel={analyticsRangeLabel} />
            </div>
            <ReasonsBreakdownCard />
          </div>
        </>
      ) : (
        <AnalyticsComingSoon />
      )}

      {/* CSV export — real, unchanged compliance workflow. Positioned
          below analytics so the pharmacist scrolls to the audit
          artifact after eyeballing performance. Available in both
          modes since it always reflects live data. */}
      <ExportSection
        from={from}
        to={to}
        setFrom={setFrom}
        setTo={setTo}
        rangeValid={rangeValid}
        downloading={downloading}
        error={error}
        lastExportInfo={lastExportInfo}
        onExport={handleExport}
        signed={!!session}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------

function PreviewBanner(): JSX.Element {
  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
      <span className="font-semibold">Demo mode</span> — the analytics
      below are sample data for demonstration only. Remove
      <code className="mx-1 rounded bg-amber-100 px-1 py-0.5">?demo=1</code>
      from the URL to see the live state.
    </div>
  );
}

// Default state when ?demo=1 is not set. The aggregation RPC hasn't
// landed yet (tracked as a follow-up in the file header) so real
// numbers aren't available; showing hardcoded placeholders by
// default was misleading. Once the RPC lands this component
// disappears — the KPI + charts render live values instead.
function AnalyticsComingSoon(): JSX.Element {
  return (
    <Card className="!p-6 text-center">
      <p className="text-sm font-medium text-ink">Analytics coming soon.</p>
      <p className="mx-auto mt-1 max-w-md text-xs text-ink-3">
        Delivery KPIs, volume trends, and failure-reason breakdowns
        populate here once your pharmacy has enough delivery history.
        In the meantime, use the CSV export below for audit-ready
        manifests.
      </p>
      <p className="mt-3 text-[11px] text-ink-3">
        Previewing?{' '}
        <a
          href="?demo=1"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Load the sample dashboard
        </a>
        .
      </p>
    </Card>
  );
}

function RangeTabs({
  value,
  onChange,
}: {
  value: RangeKey;
  onChange: (v: RangeKey) => void;
}): JSX.Element {
  return (
    <div className="inline-flex rounded-lg border border-border bg-bg-soft p-0.5">
      {RANGES.map((r) => {
        const active = value === r.key;
        return (
          <button
            key={r.key}
            type="button"
            onClick={() => onChange(r.key)}
            className={
              active
                ? 'rounded-md bg-bg px-3 py-1 text-xs font-medium text-ink shadow-sm'
                : 'rounded-md px-3 py-1 text-xs text-ink-2 hover:text-ink'
            }
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPIs

function KpiRow(): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <KpiCard
        label="Delivered"
        value={PLACEHOLDER_KPIS.delivered.value.toLocaleString()}
        delta={PLACEHOLDER_KPIS.delivered.delta}
        tone={PLACEHOLDER_KPIS.delivered.tone}
      />
      <KpiCard
        label="On-time rate"
        value={`${PLACEHOLDER_KPIS.onTimeRate.value}%`}
        delta={PLACEHOLDER_KPIS.onTimeRate.delta}
        tone={PLACEHOLDER_KPIS.onTimeRate.tone}
        valueClassName="text-signal-green"
      />
      <KpiCard
        label="Failure rate"
        value={`${PLACEHOLDER_KPIS.failureRate.value}%`}
        delta={PLACEHOLDER_KPIS.failureRate.delta}
        tone={PLACEHOLDER_KPIS.failureRate.tone}
        valueClassName="text-signal-red"
        invertDeltaTone
      />
      <KpiCard
        label="Cold chain"
        value={`${PLACEHOLDER_KPIS.coldChainCompliance.value}%`}
        delta={PLACEHOLDER_KPIS.coldChainCompliance.delta}
        tone={PLACEHOLDER_KPIS.coldChainCompliance.tone}
        valueClassName="text-signal-blue"
      />
    </div>
  );
}

function KpiCard({
  label,
  value,
  delta,
  tone,
  valueClassName,
  invertDeltaTone = false,
}: {
  label: string;
  value: string;
  delta: string;
  tone: 'up' | 'down' | 'neutral';
  valueClassName?: string;
  invertDeltaTone?: boolean;
}): JSX.Element {
  const goodWhenUp = !invertDeltaTone;
  const isUp = tone === 'up';
  const isDown = tone === 'down';
  const isGood =
    (isUp && goodWhenUp) || (isDown && !goodWhenUp) || tone === 'neutral';
  const isBad =
    (isUp && !goodWhenUp) || (isDown && goodWhenUp);
  const deltaClass =
    tone === 'neutral'
      ? 'text-ink-3'
      : isBad
        ? 'text-signal-red'
        : isGood
          ? 'text-signal-green'
          : 'text-ink-3';
  return (
    <Card className="!p-4">
      <p className="text-[11px] font-medium uppercase tracking-wide text-ink-3">
        {label}
      </p>
      <p className={'mt-1 text-2xl font-bold leading-none ' + (valueClassName ?? 'text-ink')}>
        {value}
      </p>
      <p className={'mt-1.5 text-[11px] ' + deltaClass}>{delta}</p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Volume trend — inline SVG bars, ~30 days.

function VolumeTrendCard({ rangeLabel }: { rangeLabel: string }): JSX.Element {
  const data = PLACEHOLDER_VOLUME;
  const max = Math.max(...data);
  const avg = Math.round(data.reduce((a, b) => a + b, 0) / data.length);
  const chartHeight = 140;
  const barWidth = 16;
  const barGap = 4;
  const chartWidth = data.length * (barWidth + barGap);
  const avgY = chartHeight - (avg / max) * chartHeight;
  return (
    <Card className="!p-5">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-ink">Delivery volume</h2>
        <p className="text-xs text-ink-3">
          {rangeLabel} · avg {avg}/day
        </p>
      </div>
      <div className="overflow-x-auto">
        <svg width={chartWidth} height={chartHeight + 8} role="img" aria-label="Delivery volume by day">
          <line
            x1={0}
            x2={chartWidth}
            y1={avgY}
            y2={avgY}
            stroke="#cbd5e1"
            strokeDasharray="3 4"
            strokeWidth={1}
          />
          {data.map((count, i) => {
            const barHeight = (count / max) * chartHeight;
            const x = i * (barWidth + barGap);
            const y = chartHeight - barHeight;
            return (
              <rect
                key={i}
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx={2}
                fill="#60a5fa"
              />
            );
          })}
        </svg>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Failure reasons breakdown

function ReasonsBreakdownCard(): JSX.Element {
  const data = PLACEHOLDER_REASONS;
  const max = Math.max(...data.map((d) => d.count));
  return (
    <Card className="!p-5">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-ink">Failure reasons</h2>
        <p className="text-xs text-ink-3">Selected range</p>
      </div>
      <ul className="space-y-2.5">
        {data.map((row) => {
          const pct = (row.count / max) * 100;
          return (
            <li key={row.label}>
              <div className="mb-1 flex items-baseline justify-between">
                <span className="text-xs text-ink">{row.label}</span>
                <span className="text-xs font-semibold text-ink-2">
                  {row.count}
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-soft">
                <div
                  className="h-full rounded-full bg-signal-red"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// CSV export — real endpoint, preserved from the prior Reports page.

function ExportSection({
  from,
  to,
  setFrom,
  setTo,
  rangeValid,
  downloading,
  error,
  lastExportInfo,
  onExport,
  signed,
}: {
  from: string;
  to: string;
  setFrom: (v: string) => void;
  setTo: (v: string) => void;
  rangeValid: boolean;
  downloading: boolean;
  error: string | null;
  lastExportInfo: { filename: string; rangeLabel: string } | null;
  onExport: () => Promise<void> | void;
  signed: boolean;
}): JSX.Element {
  return (
    <>
      <Card className="!p-5">
        <h2 className="text-sm font-semibold text-ink">
          Audit manifest — CSV
        </h2>
        <p className="mt-1 text-xs text-ink-2">
          One row per order in the selected date range, with chain-of-custody
          status (pickup / dropoff geofence, POD photo + signature capture,
          cold-chain flag). For the full per-delivery signed PDF, open the
          order in <span className="font-medium">Orders</span> and use its
          Audit PDF action.
        </p>

        {error ? (
          <div className="mt-4">
            <Alert tone="error">{error}</Alert>
          </div>
        ) : null}

        {lastExportInfo ? (
          <div className="mt-4">
            <Alert tone="success">
              Downloaded <span className="font-mono">{lastExportInfo.filename}</span>{' '}
              ({lastExportInfo.rangeLabel}).
            </Alert>
          </div>
        ) : null}

        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="reports-from" className="text-xs font-medium text-ink-2">
              From (inclusive)
            </label>
            <input
              id="reports-from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="reports-to" className="text-xs font-medium text-ink-2">
              To (inclusive)
            </label>
            <input
              id="reports-to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>

        {!rangeValid ? (
          <p className="mt-2 text-xs text-red-600">
            End date must be on or after start date.
          </p>
        ) : null}

        <div className="mt-4 flex items-center justify-between">
          <p className="text-[11px] text-ink-3">
            Capped at 10,000 rows per export. Narrow the range if you hit that
            limit.
          </p>
          <Button
            onClick={() => {
              void onExport();
            }}
            loading={downloading}
            disabled={!rangeValid || downloading || !signed}
          >
            Export CSV
          </Button>
        </div>
      </Card>

      <Card className="!p-4">
        <h3 className="text-xs font-semibold text-ink">CSV columns</h3>
        <ul className="mt-2 grid grid-cols-1 gap-x-4 text-xs text-ink-2 sm:grid-cols-2">
          <li>• Order created / delivered / attempted times</li>
          <li>• Pharmacy Rx number + LYN Rx order number</li>
          <li>• Patient name (RLS-scoped)</li>
          <li>• Drug name + DEA schedule</li>
          <li>• Cold-chain flag</li>
          <li>• Driver + delivery status</li>
          <li>• Pickup / delivery geofence status</li>
          <li>• POD capture + photo count + signature</li>
          <li>• Special instructions</li>
          <li>• LYN Rx order UUID (for PDF drill-through)</li>
        </ul>
      </Card>
    </>
  );
}

// Kept in the module so eslint doesn't drop the import if someone
// removes daysAgoIso() usage from RANGES; used indirectly via the
// range selector in a follow-up when custom-range wiring lands.
void daysAgoIso;
